import React, { useState, useEffect } from 'react';
import {
    getAllOrganisations, createOrganisation, updateOrganisation, deleteOrganisation,
    getAllUsers, assignUserOrganisation, updateUserRole, createUser, deleteUser,
    updateUser, toggleUserActive,
} from '../services/OrganisationService';
import {
    listInvitationCodes, createInvitationCode, deleteInvitationCode,
} from '../services/InvitationCodeService';
import '../styles/AdminPanel.css';
import '../styles/InternalNavBar.css';
import { useToast, useConfirm } from '../context/UIContext';
import { getCurrentUserRole } from '../utils/authHelper';

export default function AdminPanel() {
    const toast = useToast();
    const confirm = useConfirm();
    const currentRole = getCurrentUserRole();

    const [tab, setTab] = useState('orgs');
    const [orgs, setOrgs] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [newOrg, setNewOrg] = useState({ name: '', address: '' });
    const [editOrg, setEditOrg] = useState(null);

    const [newUser, setNewUser] = useState({ username: '', name: '', password_clear: '', email: '', role: 'user', organisation_id: '' });
    const [showNewUserForm, setShowNewUserForm] = useState(false);
    const [editUser, setEditUser] = useState(null);

    const [codes, setCodes] = useState([]);
    const [codeNote, setCodeNote] = useState('');
    const [codeExpiry, setCodeExpiry] = useState('');

    useEffect(() => {
        fetchAll();
        loadCodes();
    }, []);

    function loadCodes() {
        listInvitationCodes().then(setCodes).catch(() => setCodes([]));
    }

    async function handleCreateCode(e) {
        e.preventDefault();
        try {
            const created = await createInvitationCode({
                note: codeNote.trim() || null,
                expires_at: codeExpiry || null,
            });
            setCodeNote('');
            setCodeExpiry('');
            loadCodes();
            flash(`Einladungscode erstellt: ${created.code}`);
        } catch (err) {
            setError(err.response?.data?.detail || 'Code konnte nicht erstellt werden');
        }
    }

    async function handleDeleteCode(code) {
        if (!await confirm(`Code "${code.code}" widerrufen?`, { confirmLabel: 'Widerrufen', danger: true })) return;
        try {
            await deleteInvitationCode(code.id);
            loadCodes();
        } catch (err) {
            setError(err.response?.data?.detail || 'Code konnte nicht widerrufen werden');
        }
    }

    async function fetchAll() {
        setLoading(true);
        try {
            const [o, u] = await Promise.all([getAllOrganisations(), getAllUsers()]);
            setOrgs(o);
            setUsers(u);
        } catch (e) {
            setError('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    }

    function flash(msg) {
        setSuccess(msg);
        setTimeout(() => setSuccess(''), 3000);
    }

    function startEditUser(user) {
        setEditUser({ ...user, password_clear: '' });
    }

    async function handleCreateOrg(e) {
        e.preventDefault();
        if (!newOrg.name.trim()) return;
        try {
            await createOrganisation(newOrg);
            setNewOrg({ name: '', address: '' });
            flash('Organisation erstellt');
            fetchAll();
        } catch (e) {
            setError(e.response?.data?.detail?.message || 'Fehler beim Erstellen');
        }
    }

    async function handleUpdateOrg(e) {
        e.preventDefault();
        try {
            await updateOrganisation(editOrg.id, {
                name: editOrg.name,
                address: editOrg.address,
                is_active: editOrg.is_active,
            });
            setEditOrg(null);
            flash('Organisation aktualisiert');
            fetchAll();
        } catch (e) {
            setError('Fehler beim Aktualisieren');
        }
    }

    async function handleDeleteOrg(id) {
        if (!await confirm('Organisation wirklich löschen?', { confirmLabel: 'Löschen', danger: true })) return;
        try {
            await deleteOrganisation(id);
            flash('Organisation gelöscht');
            fetchAll();
        } catch (e) {
            setError('Fehler beim Löschen');
        }
    }

    async function handleAssignOrg(userId, orgId) {
        try {
            await assignUserOrganisation(userId, orgId || null);
            flash('Organisation zugewiesen');
            fetchAll();
        } catch (e) {
            setError('Fehler beim Zuweisen');
        }
    }

    async function handleRoleChange(userId, role) {
        try {
            await updateUserRole(userId, role);
            flash('Rolle aktualisiert');
            fetchAll();
        } catch (e) {
            setError('Fehler beim Ändern der Rolle');
        }
    }

    async function handleDeleteUser(userId) {
        if (!await confirm('Benutzer wirklich löschen?', { confirmLabel: 'Löschen', danger: true })) return;
        try {
            await deleteUser(userId);
            flash('Benutzer gelöscht');
            fetchAll();
        } catch (e) {
            setError('Fehler beim Löschen');
        }
    }

    async function handleCreateUser(e) {
        e.preventDefault();
        try {
            await createUser({
                ...newUser,
                organisation_id: newUser.organisation_id || null,
            });
            setNewUser({ username: '', name: '', password_clear: '', email: '', role: 'user', organisation_id: '' });
            setShowNewUserForm(false);
            flash('Benutzer erstellt');
            fetchAll();
        } catch (e) {
            setError(e.response?.data?.detail?.message || 'Fehler beim Erstellen');
        }
    }

    async function handleUpdateUser(e) {
        e.preventDefault();
        try {
            const dto = {
                username: editUser.username,
                email: editUser.email || null,
            };
            if (editUser.password_clear) dto.password_clear = editUser.password_clear;

            await updateUser(editUser.id, dto);

            if (editUser.is_active !== users.find(u => u.id === editUser.id)?.is_active) {
                await toggleUserActive(editUser.id, editUser.is_active);
            }

            setEditUser(null);
            flash('Benutzer aktualisiert');
            fetchAll();
        } catch (e) {
            const msg = e.response?.data?.detail;
            if (msg === 'Username already taken') {
                setError('Benutzername bereits vergeben.');
            } else {
                setError(e.response?.data?.detail?.message || 'Fehler beim Aktualisieren');
            }
        }
    }

    if (currentRole !== 'super_admin') {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh', gap: 12, fontFamily: 'system-ui, sans-serif' }}>
                <h2 style={{ fontSize: 20, margin: 0 }}>Kein Zugriff</h2>
                <p style={{ color: '#666', margin: 0 }}>Diese Seite ist nur für Super-Administratoren zugänglich.</p>
            </div>
        );
    }

    return (
        <div className="admin-panel">
            <h1>Admin Panel</h1>

            {error && (
                <div className="alert alert--error">
                    {error}
                    <button className="alert__close" onClick={() => setError('')}>×</button>
                </div>
            )}
            {success && (
                <div className="alert alert--success">{success}</div>
            )}

            <nav className="internal-nav">
                {[['orgs', 'Organisationen'], ['users', 'Benutzer'], ['codes', 'Einladungscodes']].map(([key, label]) => (
                    <button key={key} onClick={() => setTab(key)}
                        className={`internal-tab${tab === key ? ' active' : ''}`}>
                        {label}
                    </button>
                ))}
            </nav>

            {loading && <p>Laden...</p>}

            {tab === 'orgs' && (
                <div>
                    <h2>Organisation erstellen</h2>
                    <form onSubmit={handleCreateOrg} className="admin-form-inline">
                        <input
                            className="input"
                            placeholder="Name *"
                            value={newOrg.name}
                            onChange={e => setNewOrg(p => ({ ...p, name: e.target.value }))}
                            required
                        />
                        <input
                            className="input"
                            placeholder="Adresse"
                            value={newOrg.address}
                            onChange={e => setNewOrg(p => ({ ...p, address: e.target.value }))}
                        />
                        <button type="submit" className="button">Erstellen</button>
                    </form>

                    {editOrg && (
                        <form onSubmit={handleUpdateOrg} className="admin-form-card">
                            <h3>Organisation bearbeiten</h3>
                            <input
                                className="input"
                                value={editOrg.name}
                                onChange={e => setEditOrg(p => ({ ...p, name: e.target.value }))}
                            />
                            <input
                                className="input"
                                value={editOrg.address || ''}
                                onChange={e => setEditOrg(p => ({ ...p, address: e.target.value }))}
                            />
                            <label className="admin-checkbox-label">
                                <input type="checkbox" checked={editOrg.is_active}
                                    onChange={e => setEditOrg(p => ({ ...p, is_active: e.target.checked }))} />
                                Aktiv
                            </label>
                            <div className="admin-form-actions">
                                <button type="submit" className="button">Speichern</button>
                                <button type="button" className="button button-normal" onClick={() => setEditOrg(null)}>Abbrechen</button>
                            </div>
                        </form>
                    )}

                    <table className="table">
                        <thead>
                            <tr>
                                {['Name', 'Adresse', 'Status', 'Aktionen'].map(h => <th key={h}>{h}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {orgs.map(org => (
                                <tr key={org.id}>
                                    <td>{org.name}</td>
                                    <td>{org.address || '—'}</td>
                                    <td>
                                        <span className={`status-badge status-badge--${org.is_active ? 'active' : 'inactive'}`}>
                                            {org.is_active ? 'Aktiv' : 'Inaktiv'}
                                        </span>
                                    </td>
                                    <td className="admin-form-actions">
                                        <button onClick={() => setEditOrg({ ...org })} className="button">Bearbeiten</button>
                                        <button onClick={() => handleDeleteOrg(org.id)} className="button button-danger">Löschen</button>
                                    </td>
                                </tr>
                            ))}
                            {orgs.length === 0 && !loading && (
                                <tr><td colSpan={4} className="table-empty-cell">Keine Organisationen vorhanden</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'users' && (
                <div>
                    <div className="section-header">
                        <h2>Alle Benutzer</h2>
                        <button onClick={() => setShowNewUserForm(p => !p)} className="button">+ Neuer Benutzer</button>
                    </div>

                    {showNewUserForm && (
                        <form onSubmit={handleCreateUser} className="admin-form-grid">
                            <h3>Neuer Benutzer</h3>
                            <input className="input" placeholder="Benutzername *" value={newUser.username}
                                onChange={e => setNewUser(p => ({ ...p, username: e.target.value }))} required />
                            <input className="input" placeholder="Name *" value={newUser.name}
                                onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} required />
                            <input className="input" placeholder="E-Mail *" type="email" value={newUser.email}
                                onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} required />
                            <input className="input" placeholder="Passwort *" type="password" value={newUser.password_clear}
                                onChange={e => setNewUser(p => ({ ...p, password_clear: e.target.value }))} required />
                            <select className="input" value={newUser.role}
                                onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}>
                                <option value="user">Employee</option>
                                <option value="admin">Admin</option>
                                <option value="super_admin">Super Admin</option>
                            </select>
                            <select className="input" value={newUser.organisation_id}
                                onChange={e => setNewUser(p => ({ ...p, organisation_id: e.target.value }))}>
                                <option value="">Keine Organisation</option>
                                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                            <div className="admin-form-actions">
                                <button type="submit" className="button">Erstellen</button>
                                <button type="button" className="button button-normal" onClick={() => setShowNewUserForm(false)}>Abbrechen</button>
                            </div>
                        </form>
                    )}

                    {editUser && (
                        <form onSubmit={handleUpdateUser} className="admin-form-card">
                            <h3>Benutzer bearbeiten — {editUser.name}</h3>
                            <div className="admin-form-grid">
                                <div>
                                    <label className="admin-field-label">Benutzername</label>
                                    <input
                                        className="input"
                                        value={editUser.username}
                                        onChange={e => setEditUser(p => ({ ...p, username: e.target.value }))}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="admin-field-label">E-Mail</label>
                                    <input
                                        className="input"
                                        type="email"
                                        value={editUser.email || ''}
                                        onChange={e => setEditUser(p => ({ ...p, email: e.target.value }))}
                                        placeholder="E-Mail-Adresse"
                                    />
                                </div>
                                <div>
                                    <label className="admin-field-label">Neues Passwort</label>
                                    <input
                                        className="input"
                                        type="password"
                                        value={editUser.password_clear}
                                        onChange={e => setEditUser(p => ({ ...p, password_clear: e.target.value }))}
                                        placeholder="Leer lassen = unverändert"
                                    />
                                </div>
                                <div>
                                    <label className="admin-field-label">Status</label>
                                    <label className="admin-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={editUser.is_active}
                                            onChange={e => setEditUser(p => ({ ...p, is_active: e.target.checked }))}
                                        />
                                        Konto aktiv
                                    </label>
                                </div>
                            </div>
                            <div className="admin-form-actions">
                                <button type="submit" className="button">Speichern</button>
                                <button type="button" className="button button-normal" onClick={() => setEditUser(null)}>Abbrechen</button>
                            </div>
                        </form>
                    )}

                    <div className="admin-table-wrap">
                    <table className="table admin-users-table">
                        <thead>
                            <tr>
                                {['Name', 'Benutzername', 'E-Mail', 'Rolle', 'Organisation', 'Status', 'Aktionen'].map(h => <th key={h}>{h}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id} className={!user.is_active ? 'row--inactive' : ''}>
                                    <td className="admin-cell-text" title={user.name}>{user.name}</td>
                                    <td className="admin-cell-text" title={user.username}>{user.username}</td>
                                    <td className="admin-cell-text" title={user.email || ''}>{user.email || '—'}</td>
                                    <td>
                                        <select className="admin-select" value={user.role}
                                            onChange={e => handleRoleChange(user.id, e.target.value)}>
                                            <option value="user">Employee</option>
                                            <option value="admin">Admin</option>
                                            <option value="super_admin">Super Admin</option>
                                        </select>
                                    </td>
                                    <td>
                                        <select className="admin-select" value={user.organisation_id || ''}
                                            onChange={e => handleAssignOrg(user.id, e.target.value)}>
                                            <option value="">Keine</option>
                                            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                        </select>
                                    </td>
                                    <td>
                                        <span className={`status-badge status-badge--${user.is_active ? 'active' : 'inactive'}`}>
                                            {user.is_active ? 'Aktiv' : 'Inaktiv'}
                                        </span>
                                    </td>
                                    <td className="admin-form-actions">
                                        <button onClick={() => startEditUser(user)} className="button">Bearbeiten</button>
                                        <button onClick={() => handleDeleteUser(user.id)} className="button button-danger">Löschen</button>
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && !loading && (
                                <tr><td colSpan={7} className="table-empty-cell">Keine Benutzer vorhanden</td></tr>
                            )}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}

            {tab === 'codes' && (
                <div>
                    <h2>Einladungscode erstellen</h2>
                    <p style={{ color: 'var(--color-text-secondary)', marginTop: 0 }}>
                        Nur wer einen gültigen Code hat, kann sich registrieren. Jeder Code ist
                        einmalig verwendbar.
                    </p>
                    <form onSubmit={handleCreateCode} className="admin-form-inline">
                        <input
                            className="input"
                            type="text"
                            placeholder="Notiz (optional, z.B. „Kunde Müller“)"
                            value={codeNote}
                            onChange={e => setCodeNote(e.target.value)}
                        />
                        <input
                            className="input"
                            type="date"
                            title="Ablaufdatum (optional)"
                            value={codeExpiry}
                            onChange={e => setCodeExpiry(e.target.value)}
                        />
                        <button type="submit" className="button">Code erstellen</button>
                    </form>

                    <table className="admin-table" style={{ marginTop: 16 }}>
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Notiz</th>
                                <th>Status</th>
                                <th>Läuft ab</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {codes.length === 0 && (
                                <tr><td colSpan={5} style={{ color: 'var(--color-text-muted)' }}>Noch keine Codes.</td></tr>
                            )}
                            {codes.map(c => (
                                <tr key={c.id}>
                                    <td style={{ fontFamily: 'monospace' }}>{c.code}</td>
                                    <td>{c.note || '—'}</td>
                                    <td>
                                        {c.status === 'available' ? 'Verfügbar'
                                            : c.status === 'used' ? 'Verwendet'
                                            : 'Abgelaufen'}
                                    </td>
                                    <td>{c.expires_at ? c.expires_at.slice(0, 10) : '—'}</td>
                                    <td>
                                        {c.status === 'available' && (
                                            <button className="button button-danger"
                                                    onClick={() => handleDeleteCode(c)}>
                                                Widerrufen
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
